// MenusGestion.jsx
import { useState, useEffect } from "react";
import {
  Menu,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  RefreshCw,
  Loader2,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Power,
  PowerOff,
  Check,
  Package,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import Swal from "sweetalert2";

const MenusGestion = () => {
  const [menus, setMenus] = useState([]);
  const [parentOptions, setParentOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState(new Set());
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    icon: "",
    route: "",
    parent_id: null,
    order_index: 0,
    active: true,
    es_modulo: false,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadMenus();
    loadParentOptions();
  }, []);

  const getApiUrl = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) return "http://localhost:8000/api";
    return apiUrl.endsWith("/api") ? apiUrl : `${apiUrl}/api`;
  };

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const loadMenus = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getApiUrl()}/menus`, {
        headers: getHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setMenus(data.data || []);
        const level1Ids = (data.data || []).map((m) => m.id);
        setExpandedMenus(new Set(level1Ids));
      }
    } catch (error) {
      console.error("Error:", error);
      Swal.fire("Error", "No se pudieron cargar los menús", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadParentOptions = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/menus/parent-options`, {
        headers: getHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setParentOptions(data.data || []);
      }
    } catch (error) {
      console.error("Error loading parent options:", error);
    }
  };

  const toggleMenuExpansion = (menuId) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuId)) {
      newExpanded.delete(menuId);
    } else {
      newExpanded.add(menuId);
    }
    setExpandedMenus(newExpanded);
  };

  const handleOpenModal = (menu = null) => {
    if (menu) {
      setEditingMenu(menu);
      setFormData({
        name: menu.name,
        slug: menu.slug,
        icon: menu.icon || "",
        route: menu.route || "",
        parent_id: menu.parent_id || null,
        order_index: menu.order_index,
        active: menu.active,
        es_modulo: menu.es_modulo || false,
      });
    } else {
      setEditingMenu(null);
      setFormData({
        name: "",
        slug: "",
        icon: "",
        route: "",
        parent_id: null,
        order_index: 0,
        active: true,
        es_modulo: false,
      });
    }
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMenu(null);
    setErrors({});
  };

  const generateSlug = (name) =>
    name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const handleNameChange = (value) => {
    setFormData({
      ...formData,
      name: value,
      slug: editingMenu ? formData.slug : generateSlug(value),
    });
  };

  // Al seleccionar un padre → forzar es_modulo=false (solo nivel 1 puede ser contratable)
  const handleParentChange = (value) => {
    setFormData({
      ...formData,
      parent_id: value || null,
      es_modulo: value ? false : formData.es_modulo,
    });
  };

  const handleSubmit = async () => {
    setErrors({});
    const url = editingMenu
      ? `${getApiUrl()}/menus/${editingMenu.id}`
      : `${getApiUrl()}/menus`;
    const method = editingMenu ? "PUT" : "POST";

    try {
      const payload = {
        ...formData,
        parent_id: formData.parent_id === "" ? null : formData.parent_id,
        es_modulo: formData.parent_id ? false : formData.es_modulo,
      };

      const response = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: editingMenu ? "Menú actualizado" : "Menú creado",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });
        handleCloseModal();
        loadMenus();
        loadParentOptions();
      } else {
        if (data.errors) setErrors(data.errors);
        throw new Error(data.message || "Error al guardar el menú");
      }
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  const handleDelete = async (menuId, menuName) => {
    const result = await Swal.fire({
      title: "¿Eliminar menú?",
      html: `Se eliminará <strong>${menuName}</strong> y todos sus permisos.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (result.isConfirmed) {
      try {
        const response = await fetch(`${getApiUrl()}/menus/${menuId}`, {
          method: "DELETE",
          headers: getHeaders(),
        });
        const data = await response.json();
        if (response.ok) {
          Swal.fire("Eliminado", data.message, "success");
          loadMenus();
          loadParentOptions();
        } else {
          throw new Error(data.message || "Error al eliminar");
        }
      } catch (error) {
        Swal.fire("Error", error.message, "error");
      }
    }
  };

  const handleToggleActive = async (menuId) => {
    try {
      const response = await fetch(
        `${getApiUrl()}/menus/${menuId}/toggle-active`,
        { method: "PATCH", headers: getHeaders() }
      );
      const data = await response.json();
      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: data.message,
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 1500,
        });
        loadMenus();
      } else {
        throw new Error(data.message || "Error al cambiar estado");
      }
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  const getLevelStyle = (level) => {
    switch (level) {
      case 0: return "bg-blue-50 border-blue-200";
      case 1: return "bg-green-50 border-green-200";
      default: return "bg-purple-50 border-purple-200";
    }
  };

  const getLevelBadgeColor = (level) => {
    switch (level) {
      case 0: return "bg-blue-100 text-blue-800";
      case 1: return "bg-green-100 text-green-800";
      default: return "bg-purple-100 text-purple-800";
    }
  };

  // ── Ítem del árbol ────────────────────────────────────
  const MenuTreeItem = ({ menu, level = 0 }) => {
    const hasChildren = menu.children && menu.children.length > 0;
    const isExpanded = expandedMenus.has(menu.id);
    const isNivel1 = level === 0;

    return (
      <div>
        <div
          className={`flex items-center justify-between p-3 rounded-lg border
            ${getLevelStyle(level)} hover:shadow-sm transition-all`}
          style={{ marginLeft: `${level * 24}px` }}
        >
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {hasChildren ? (
              <button
                className="h-6 w-6 flex items-center justify-center hover:bg-white/60 rounded"
                onClick={() => toggleMenuExpansion(menu.id)}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <div className="w-6" />
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-1.5">
                <span className="font-medium text-sm truncate">{menu.name}</span>

                <Badge className={`text-xs ${getLevelBadgeColor(menu.level)}`}>
                  Nivel {menu.level + 1}
                </Badge>

                {menu.active ? (
                  <Badge variant="default" className="text-xs">
                    <Check className="h-3 w-3 mr-1" />Activo
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">
                    <X className="h-3 w-3 mr-1" />Inactivo
                  </Badge>
                )}

                {/* Solo nivel 1 muestra el badge Contratable */}
                {isNivel1 && menu.es_modulo && (
                  <Badge className="text-xs bg-amber-100 text-amber-800 border border-amber-300">
                    <Package className="h-3 w-3 mr-1" />
                    Contratable
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                {menu.slug && <span className="font-mono">/{menu.slug}</span>}
                {menu.route && <span className="text-blue-600">{menu.route}</span>}
              </div>
            </div>
          </div>

          {/* Acciones */}
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={() => handleOpenModal(menu)}>
              <Edit className="h-3 w-3" />
            </Button>
            <Button
              variant={menu.active ? "secondary" : "default"}
              size="sm"
              onClick={() => handleToggleActive(menu.id)}
              title={menu.active ? "Desactivar" : "Activar"}
            >
              {menu.active ? <PowerOff className="h-3 w-3" /> : <Power className="h-3 w-3" />}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleDelete(menu.id, menu.name)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Hijos */}
        {hasChildren && isExpanded && (
          <div className="space-y-1 mt-1">
            {menu.children.map((child) => (
              <MenuTreeItem key={child.id} menu={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const isFormNivel1 = !formData.parent_id;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Cargando menús...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Menu className="h-5 w-5 text-blue-600" />
              <CardTitle>Gestión de Menús</CardTitle>
              <Badge variant="secondary">{menus.length} menús</Badge>
            </div>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm" onClick={loadMenus}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Actualizar
              </Button>
              <Button size="sm" onClick={() => handleOpenModal()}>
                <Plus className="h-4 w-4 mr-1" />
                Nuevo Menú
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-2">
            <Badge className="text-xs bg-amber-100 text-amber-800 border border-amber-300">
              <Package className="h-3 w-3 mr-1" />
              Contratable
            </Badge>
            <span className="text-xs text-gray-400">
              = visible en Módulos del Sistema y asignable a empresas
            </span>
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-2">
            {menus.map((menu) => (
              <MenuTreeItem key={menu.id} menu={menu} />
            ))}
          </div>
          {menus.length === 0 && (
            <div className="text-center py-12">
              <Menu className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No hay menús registrados</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl my-8">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center">
                <Menu className="h-5 w-5 mr-2 text-blue-600" />
                {editingMenu ? "Editar Menú" : "Nuevo Menú"}
              </CardTitle>
            </CardHeader>

            <CardContent className="pt-6">
              <div className="space-y-4">

                {/* Nombre + Slug */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Nombre <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder="Dashboard, Usuarios..."
                      className={errors.name ? "border-red-500" : ""}
                    />
                    {errors.name && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />{errors.name[0]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Slug <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={formData.slug}
                      onChange={(e) =>
                        setFormData({ ...formData, slug: e.target.value })
                      }
                      placeholder="dashboard, usuarios"
                      className={errors.slug ? "border-red-500" : ""}
                    />
                    {errors.slug && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />{errors.slug[0]}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      Solo minúsculas, números y guiones
                    </p>
                  </div>
                </div>

                {/* Icono + Ruta */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Icono (Lucide)</label>
                    <Input
                      value={formData.icon}
                      onChange={(e) =>
                        setFormData({ ...formData, icon: e.target.value })
                      }
                      placeholder="Home, Truck, MapPin..."
                    />
                    <p className="text-xs text-gray-400 mt-1">Nombre exacto del ícono</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Ruta</label>
                    <Input
                      value={formData.route}
                      onChange={(e) =>
                        setFormData({ ...formData, route: e.target.value })
                      }
                      placeholder="/dashboard, /usuarios"
                    />
                  </div>
                </div>

                {/* Menú Padre + Orden */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Menú Padre</label>
                    <select
                      value={formData.parent_id || ""}
                      onChange={(e) => handleParentChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm
                        focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Sin padre (Nivel 1)</option>
                      {parentOptions.map((option) => (
                        <option
                          key={option.id}
                          value={option.id}
                          disabled={editingMenu && option.id === editingMenu.id}
                        >
                          {option.level === 0 ? "📁 " : "  📄 "}
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Orden</label>
                    <Input
                      type="number"
                      value={formData.order_index}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          order_index: parseInt(e.target.value) || 0,
                        })
                      }
                      min="0"
                    />
                  </div>
                </div>

                {/* Switch Activo */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <label className="text-sm font-medium">Activo</label>
                  <Switch
                    checked={formData.active}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, active: checked })
                    }
                  />
                </div>

                {/* Switch es_modulo — solo si es nivel 1 */}
                {isFormNivel1 ? (
                  <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div>
                      <label className="text-sm font-medium text-amber-900 flex items-center gap-1.5">
                        <Package className="h-4 w-4 text-amber-600" />
                        ¿Es módulo contratable?
                      </label>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Aparecerá en <strong>Módulos del Sistema</strong> y
                        podrá asignarse a empresas.
                      </p>
                    </div>
                    <Switch
                      checked={formData.es_modulo}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, es_modulo: checked })
                      }
                    />
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Los submenús no pueden ser módulos contratables.
                  </p>
                )}

                {/* Botones */}
                <div className="flex space-x-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={handleCloseModal}>
                    <X className="h-4 w-4 mr-1" />
                    Cancelar
                  </Button>
                  <Button className="flex-1" onClick={handleSubmit}>
                    <Save className="h-4 w-4 mr-1" />
                    {editingMenu ? "Actualizar" : "Crear"}
                  </Button>
                </div>

              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MenusGestion;
